import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Popconfirm, Row, Col } from 'antd';
import { ColumnsType } from 'antd/es/table/Table';
import {
  UserOutput,
  UserListSort,
  UserRole,
  UsersListFilterInput,
} from 'gql/types';
import { Button, TooltipWithOverflow } from 'modules/common/components';
import { TruncatedTagList } from 'modules/common/components/TruncatedTagList';
import { ISortOption } from 'modules/common/interfaces';
import { createSortOrderFactory } from 'modules/common/utils/sort/createSortOrderFactory';
import { getTranslation } from 'utils/getTranslation';

const t = getTranslation('admin.UsersListPage');
const userRoles = Object.values(UserRole);
const internalUserFilters = userRoles.reduce((roleFilters, role) => {
  if (role === UserRole.Owner) {
    return roleFilters;
  }
  return [
    ...roleFilters,
    {
      text: t(`${role[0].toLowerCase()}${role.substring(1)}`),
      value: role,
    },
  ];
}, [] as any[]);

type ColumnsProps = {
  deleteHandler?: (userId: string) => void;
  onClickEditIcon?: (userId: string) => void;
  canDelete: boolean;
  canUpdateUserInfo: boolean;
  currentUserId?: string;
  showSorterTooltip?: boolean;
  sort?: ISortOption<UserListSort>;
  filterValue?: UsersListFilterInput;
};

export const getColumns = ({
  onClickEditIcon,
  deleteHandler,
  canDelete,
  canUpdateUserInfo,
  currentUserId,
  showSorterTooltip,
  sort,
  filterValue,
}: ColumnsProps): ColumnsType<UserOutput> => {
  const getDefaultSort = createSortOrderFactory<UserListSort>(sort);

  return [
    {
      title: t('firstName'),
      render: (record: UserOutput) => record.firstName,
      key: UserListSort.FirstName,
      sorter: true,
      showSorterTooltip,
      align: 'center',
      sortOrder: getDefaultSort(UserListSort.FirstName),
      width: 120,
    },
    {
      title: t('lastName'),
      render: (record: UserOutput) => record.lastName,
      key: UserListSort.LastName,
      sorter: true,
      showSorterTooltip,
      align: 'center',
      sortOrder: getDefaultSort(UserListSort.LastName),
      width: 120,
    },
    {
      title: t('email'),
      render: ({ email }: UserOutput) => (
        <TooltipWithOverflow text={email} copyable />
      ),
      key: UserListSort.Email,
      sorter: true,
      showSorterTooltip,
      align: 'center',
      sortOrder: getDefaultSort(UserListSort.Email),
      width: 160,
    },
    {
      title: t('role'),
      key: 'role',
      render: ({ role }: UserOutput) =>
        role
          ? t(`${role.value[0].toLowerCase()}${role.value.substring(1)}`)
          : '',
      align: 'center',
      filters: internalUserFilters,
      filteredValue: filterValue?.roles || null,
      width: 100,
    },

    {
      title: t('assignedRegions'),
      render: ({ assignedRegions }: UserOutput) => {
        return <TruncatedTagList items={assignedRegions} />;
      },
      align: 'center',
      width: 120,
    },
    {
      title: t('assignedLocations'),
      render: ({ assignedLocations }: UserOutput) => {
        return (
          <TruncatedTagList items={assignedLocations} maxTextLength={15} />
        );
      },
      align: 'center',
      width: 140,
    },
    {
      title: t('assignedOffice'),
      render: ({ assignedOffice }: UserOutput) => assignedOffice?.name || '-',
      align: 'center',
      width: 120,
    },
    {
      title: t('productRegionPreference'),
      render: ({ productRegionPreference }: UserOutput) =>
        productRegionPreference?.name || '-',
      align: 'center',
      width: 100,
    },
    {
      render: (record: UserOutput) => {
        const isMe = currentUserId === record.id;
        return !isMe ? (
          <Row justify="space-around">
            {canUpdateUserInfo && (
              <Col>
                <Button
                  type="text"
                  onClick={() => onClickEditIcon && onClickEditIcon(record.id)}
                  icon={<EditOutlined />}
                />
              </Col>
            )}
            {canDelete && (
              <Col>
                <Popconfirm
                  title={t('areYouSureYouWantToDeleteUser')}
                  onConfirm={() => deleteHandler && deleteHandler(record.id)}
                  okText={t('yes')}
                  cancelText={t('no')}
                >
                  <Button type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Col>
            )}
          </Row>
        ) : (
          t('me')
        );
      },
      align: 'center',
      width: 60,
    },
  ];
};
